import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import shortfyLogo from "../../assets/images/Shortify-Main-Page-Logo.svg";
import "../../styles/Header.css";
import { useClientPrincipal } from "../../context/ClientPrincipalContext";
import { useAuthActions } from "../../hooks/useAuthActions";
import { useRoles } from "../../hooks/useRoles";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { notifyLogout } = useAuthActions();
  const { hasEventAdminRole } = useRoles();
  const [showDropdown, setShowDropdown] = useState(false);
  const userRef = useRef<HTMLSpanElement>(null);
  const { clientPrincipal } = useClientPrincipal();

  const username = clientPrincipal?.userDetails?.split("@")[0] || "";
  const isLoggedIn = !!username;

  const handleLogoClick = () => {
    navigate(isLoggedIn ? "/" : "/login");
  };

  const handleLogout = useCallback(async () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("expiresAt");

      notifyLogout();

      window.location.href =
        "/.auth/logout?post_logout_redirect_uri=" +
        encodeURIComponent(window.location.origin + "/login");
    } catch (error) {
      window.location.href = "/login";
    }
  }, [notifyLogout]);

  const handleClick = useCallback((event: MouseEvent) => {
    if (userRef.current && !userRef.current.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [showDropdown, handleClick]);

  const handleMenuItem = (key: string) => {
    setShowDropdown(false);
    switch (key) {
      case "/":
        navigate("/");
        break;
      case "my-urls":
        navigate("/my-urls");
        break;
      case "my-registrations":
        navigate("/my-registrations");
        break;
      case "cgi-events":
        navigate("/cgi-events");
        break;
      case "logout":
        handleLogout();
        break;
      default:
        break;
    }
  };

  return (
    <header className="cgih-header">
      <div className="container cgih-header-inner">
        <div className="cgih-left">
          <span onClick={handleLogoClick} style={{ cursor: "pointer" }}>
            <div className="cgih-logo-wrapper">
              <img
                src={shortfyLogo}
                alt="CGI logo"
                className="cgih-cgi-logo-img"
              />
            </div>
          </span>

          <span className="cgih-divider-left" />
          <span className="cgih-title">Powered by GTO Germany</span>
        </div>
        <div className="cgih-right">
          {isLoggedIn && (
            <>
              <span
                className="cgih-user"
                ref={userRef}
                onClick={() => setShowDropdown((v) => !v)}
                style={{
                  position: "relative",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <img
                  src="https://brand.cgi.com/images/ico_maccount.svg"
                  alt="User icon"
                  style={{ width: "22px", height: "22px", marginLeft: "2px" }}
                />
                <span
                  className="cgih-chevron"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <linearGradient
                        id="chevron-gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="2%" stopColor="#e31937" />
                        <stop offset="44%" stopColor="#a82465" />
                        <stop offset="84%" stopColor="#5236ab" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="url(#chevron-gradient)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {showDropdown && (
                  <div className="cgih-user-dropdown" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handleMenuItem("/")}>
                      Homepage
                    </button>
                    <div className="cgih-user-dropdown-divider" />
                    <button onClick={() => handleMenuItem("my-urls")}>
                      My URLs
                    </button>
                    <div className="cgih-user-dropdown-divider" />
                    {hasEventAdminRole() && (
                      <>
                        <button onClick={() => handleMenuItem("cgi-events")}>
                          CGI Events
                        </button>
                        <div className="cgih-user-dropdown-divider" />
                      </>
                    )}
                    <button onClick={() => handleMenuItem("my-registrations")}>
                      My Registrations
                    </button>
                    <div className="cgih-user-dropdown-divider" />
                    <button onClick={() => handleMenuItem("logout")}>
                      Logout
                    </button>
                  </div>
                )}
              </span>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
